import axios from "axios";

const baseUrl = "/api/tool-monitoring";

const throwResponseData = (error) => {
  throw error.response?.data ?? error;
};

/*
 * How far every watched tool in the company has run against its schedule.
 *
 * One entry per active, non-calendar tool schedule — not per tool. A tool measured in hours by
 * one schedule and in cycles by another answers twice, and neither reading summarises the
 * other. The server returns them worst first (level, then percent), which is what lets a caller
 * fold to one row per tool without inventing a ranking of its own.
 *
 * `toolId` narrows it to a single tool for callers that already know which one they mean.
 */
export const fetchToolServiceStatuses = async (companyKey, toolId) => {
  try {
    const response = await axios.get(`${baseUrl}/status`, {
      params: { companyKey, toolId },
    });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

/*
 * When this tool was serviced, newest first.
 *
 * The log is written in one place only — closing a work order that names the tool — so an
 * empty answer means "never serviced", not "we have no data yet". That distinction is what
 * the panel's empty state says out loud.
 *
 * Entries carry ids, not names: `servicedByUserId` and `cmmsWorkOrderId` are resolved by the
 * caller, which already knows who is on the page. `take` caps the list server-side and
 * defaults to 50 there; nothing here paginates.
 */
export const fetchToolServiceHistory = async (companyKey, toolId, take) => {
  try {
    const response = await axios.get(`${baseUrl}/tools/${toolId}/service-log`, {
      params: { companyKey, take },
    });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

/*
 * What sits in the machine the asset node belongs to.
 *
 * Read-only and advisory. The tool is never sent back when the issue is reported — the
 * server resolves it again on POST — so nothing here becomes an input.
 *
 * Answers { machineId, toolId, toolName }, any of which may be null. A toolName without a
 * toolId means the production order names a tool that has no row in the tool register.
 */
export const fetchActiveTool = async (companyKey, assetNodeId) => {
  try {
    const response = await axios.get(`${baseUrl}/active-tool`, {
      params: { companyKey, assetNodeId },
    });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};
